import axios, { CancelToken } from "axios";
import { debounce } from "@/utils/utils";

export const SEARCH_DEBOUNCE_MS = 500;

export type SearchPayload = {
  immediate?: boolean;
  [key: string]: unknown;
};

export type CancelTokenConfig = {
  cancelToken: CancelToken;
};

type CancelableRunResult<T> =
  | { data: T; settled: true }
  | { data: undefined; settled: false };

export const isRequestCancelled = (error: unknown): boolean => axios.isCancel(error);

const createCancelableRequest = () => {
  let source: ReturnType<typeof axios.CancelToken.source> | null = null;
  let requestId = 0;

  const cancel = (reason = "Request cancelled") => {
    source?.cancel(reason);
    source = null;
  };

  const run = async <T>(
    request: (config: CancelTokenConfig) => Promise<T>,
    reason = "Superseded by new request",
  ): Promise<CancelableRunResult<T>> => {
    cancel(reason);

    const current = axios.CancelToken.source();
    source = current;
    const id = ++requestId;

    try {
      const data = await request({ cancelToken: current.token });
      if (requestId !== id) {
        return { data: undefined, settled: false };
      }
      return { data, settled: true };
    } catch (error) {
      if (isRequestCancelled(error) || requestId !== id) {
        return { data: undefined, settled: false };
      }
      throw error;
    }
  };

  const destroy = (reason = "Request cancelled") => {
    cancel(reason);
    requestId++;
  };

  return { run, destroy };
};

type SearchControllerOptions<T> = {
  fetcher: (
    params: Record<string, unknown>,
    config: CancelTokenConfig,
  ) => Promise<T | null | undefined>;
  onLoading?: (loading: boolean) => void;
  onSuccess: (data: T) => void;
  onError?: (error: unknown) => void;
};

export const createSearchController = <T>({
  fetcher,
  onLoading,
  onSuccess,
  onError,
}: SearchControllerOptions<T>) => {
  const cancelable = createCancelableRequest();

  const fetch = async (params: Record<string, unknown> = {}) => {
    onLoading?.(true);
    let settled = false;

    try {
      const { data, settled: isSettled } = await cancelable.run((config) => fetcher(params, config));
      settled = isSettled;

      if (!isSettled || data == null) {
        return;
      }

      onSuccess(data);
    } catch (error) {
      settled = true;
      onError?.(error);
    } finally {
      if (settled) {
        onLoading?.(false);
      }
    }
  };

  const debouncedFetch = debounce(fetch, SEARCH_DEBOUNCE_MS);

  return {
    search(payload: SearchPayload = {}) {
      const { immediate = false, ...params } = payload;

      if (immediate === true) {
        debouncedFetch.cancel();
        fetch(params);
        return;
      }

      debouncedFetch(params);
    },
    fetch,
    destroy() {
      debouncedFetch.cancel();
      cancelable.destroy();
    },
  };
};
